import { useT } from '@powerportalspro/react';
import {
  AggregateDataverseChartDataSource,
  QueryAggregate,
  DateGrouping,
  ChartLegendPosition,
  ChartOrientation,
  ChartType,
} from '@powerportalspro/react-charts';
import { DataverseChart, Section, SectionColumn } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';

// Two bar charts over the opportunities. The first puts each year's won and lost revenue side by
// side: `seriesColumn` splits every year into one bar per status, and `stacked` is left off so the
// bars stand next to each other rather than on top. The second lies on its side
// (`orientation={ChartOrientation.Horizontal}`), which leaves room for long labels: the open
// pipeline by the customer's industry, a column of the account rather than the opportunity,
// reached through `groupByLinkedEntity`. An opportunity whose customer is a contact has no
// industry, and is left out.

const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.ChartDemos.BarChartShowcaseMainDemo';

export function BarChartShowcaseMainSample() {
  const t = useT();

  // Kept in useMemo so each chart keeps one source object, and so loads once.
  const wonAndLostByYear = useMemo(
    () =>
      new AggregateDataverseChartDataSource({
        tableName: 'opportunity',
        groupByColumn: 'actualclosedate',
        groupByDateGrouping: DateGrouping.Year,
        seriesColumn: 'statecode',
        aggregateColumn: 'estimatedvalue',
        aggregate: QueryAggregate.Sum,
        // Closed opportunities only: won (1) and lost (2).
        filterXml: "<filter><condition attribute='statecode' operator='ne' value='0' /></filter>",
      }),
    [],
  );

  const pipelineByIndustry = useMemo(
    () =>
      new AggregateDataverseChartDataSource({
        tableName: 'opportunity',
        groupByColumn: 'industrycode',
        // The opportunity's customer is an account; its industry is the account's column.
        groupByLinkedEntity: { tableName: 'account', from: 'accountid', to: 'customerid' },
        aggregateColumn: 'estimatedvalue',
        aggregate: QueryAggregate.Sum,
        filterXml: "<filter><condition attribute='statecode' operator='eq' value='0' /></filter>",
        singleSeriesLabel: t(`${STRINGS_BASE}.pipeline-series-label`),
      }),
    [t],
  );

  return (
    <Section borderVisible={false}>
      <SectionColumn>
        <DataverseChart
          dataSource={wonAndLostByYear}
          type={ChartType.Bar}
          title={t(`${STRINGS_BASE}.won-lost-title`)}
          yAxisPrefix="$"
          height="420px"
        />
      </SectionColumn>
      <SectionColumn>
        <DataverseChart
          dataSource={pipelineByIndustry}
          type={ChartType.Bar}
          orientation={ChartOrientation.Horizontal}
          title={t(`${STRINGS_BASE}.pipeline-by-industry-title`)}
          xAxisPrefix="$"
          legendPosition={ChartLegendPosition.Hidden}
          height="420px"
        />
      </SectionColumn>
    </Section>
  );
}
